import React from "react";
import { useAnketaI18n } from "src/internationalization";

/*
 * Tarnkappe - AI without losing control.
 *
 * Everything that identifies a person or system is pseudonymised before it
 * reaches a model; technical values (CVE, ports, versions) stay so the model
 * can still reason. The dictionary key stays in memory on the appliance, the
 * answer is translated back into clear text, and AI-proposed actions need an
 * approval - the customer's team implements. All data is fictional example
 * data (never real hostnames on the public site).
 */

const tp = "anketa.autonubil.home.tarnkappe";

const ROWS: { key: string; label: string; real: string; tarn: string; keep?: boolean }[] = [
  { key: "person", label: "Person", real: "Klaus Thaler", tarn: "Paul Weber" },
  { key: "mail", label: "E-Mail", real: "k.thaler@corp.local", tarn: "p.weber@excalibur.example" },
  { key: "host", label: "Host", real: "srv-fibu-01", tarn: "srv-app-17" },
  { key: "ip", label: "IP", real: "10.12.4.7", tarn: "172.20.9.7" },
  { key: "cve", label: "CVE", real: "CVE-2024-3094", tarn: "CVE-2024-3094", keep: true },
];

const Emblem: React.FC = () => (
  <svg width="64" height="64" viewBox="0 0 120 120" aria-hidden="true">
    <circle cx="60" cy="60" r="55" fill="none" stroke="var(--ak-accent)" strokeWidth="2.5" strokeDasharray="22 6 5 6" strokeLinecap="round" />
    <path d="M60 16 L98 38 L98 82 L60 104 L22 82 L22 38 Z" fill="var(--ak-accent-tint)" stroke="var(--ak-accent)" strokeWidth="3" strokeLinejoin="round" />
    <path d="M60 30 C44 30 35.5 44.5 35.5 61 L35.5 84 C45 79.5 53 81.5 60 88 C67 81.5 75 79.5 84.5 84 L84.5 61 C84.5 44.5 76 30 60 30 Z" fill="var(--ak-navy-2, #112338)" />
    <path d="M46.5 61 Q52 57 57.5 61 Q52 64 46.5 61 Z M62.5 61 Q68 57 73.5 61 Q68 64 62.5 61 Z" fill="var(--ak-accent-2)" />
  </svg>
);

const DataCard: React.FC<{ title: string; tag: string; mode: "real" | "tarn" }> = ({ title, tag, mode }) => (
  <div
    style={{
      background: "var(--ak-surface)",
      border: `1px solid ${mode === "tarn" ? "var(--ak-accent-border)" : "var(--ak-border)"}`,
      borderRadius: 16,
      padding: "20px 22px 22px",
      boxShadow: "0 1px 4px var(--ak-shadow)",
    }}
  >
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
      <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ak-accent)" }}>{title}</span>
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", padding: "3px 9px", borderRadius: 999, border: "1px solid var(--ak-border)", color: "var(--ak-text-muted)" }}>
        {tag}
      </span>
    </div>
    <dl style={{ margin: "14px 0 0", display: "grid", gridTemplateColumns: "auto 1fr", rowGap: 8, columnGap: 16 }}>
      {ROWS.map((r) => (
        <React.Fragment key={r.key}>
          <dt style={{ fontSize: "0.8rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--ak-text-muted)" }}>{r.label}</dt>
          <dd
            style={{
              margin: 0,
              textAlign: "right",
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: "0.9rem",
              overflowWrap: "anywhere",
              color: r.keep ? "var(--ak-text)" : mode === "tarn" ? "var(--ak-accent)" : "var(--ak-text)",
            }}
          >
            {mode === "real" ? r.real : r.tarn}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  </div>
);

export const AutonubilTarnkappe: React.FC = () => {
  const { translate } = useAnketaI18n();

  const steps = [
    translate(`${tp}.step1`, { defaultMessage: "Disguise" } as any),
    translate(`${tp}.step2`, { defaultMessage: "AI analyses" } as any),
    translate(`${tp}.step3`, { defaultMessage: "Conclusion" } as any),
    translate(`${tp}.step4`, { defaultMessage: "Undisguised on return" } as any),
  ];

  return (
    <section id="tarnkappe" style={{ background: "var(--ak-surface-2)", padding: "clamp(64px, 9vw, 100px) clamp(24px, 5vw, 48px)" }}>
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <div style={{ maxWidth: 760, marginBottom: 40 }}>
          <p style={{ margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ak-accent)" }}>
            {translate(`${tp}.eyebrow`, { defaultMessage: "AI extension · Tarnkappe" } as any)}
          </p>
          <h2 style={{ margin: "12px 0 0", fontSize: "clamp(1.9rem, 3.4vw, 2.8rem)", fontWeight: 800, letterSpacing: "-0.025em", lineHeight: 1.1, color: "var(--ak-text)" }}>
            {translate(`${tp}.title.pre`, { defaultMessage: "The AI only sees" } as any)}{" "}
            <span style={{ background: "linear-gradient(120deg, var(--ak-accent) 0%, var(--ak-accent-2) 100%)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent", paddingBottom: "0.1em" }}>
              {translate(`${tp}.title.highlight`, { defaultMessage: "a disguised world." } as any)}
            </span>
          </h2>
          <p style={{ margin: "16px 0 0", fontSize: "1.05rem", lineHeight: 1.65, color: "var(--ak-text-secondary)" }}>
            {translate(`${tp}.subtitle`, {
              defaultMessage:
                "Everything that identifies people or systems is pseudonymised before it reaches the model - CVEs, ports and versions stay, so the AI can still reason. You get the answer back in clear text.",
            } as any)}
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))", gap: 24, alignItems: "center" }}>
          <DataCard mode="real" title={translate(`${tp}.real.title`, { defaultMessage: "Your house" } as any)} tag={translate(`${tp}.real.tag`, { defaultMessage: "Real data" } as any)} />
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 10 }}>
            <Emblem />
            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--ak-accent)" }}>Tarnkappe</span>
            <span aria-hidden="true" style={{ fontSize: "1.6rem", color: "var(--ak-accent)" }}>⇄</span>
            <span style={{ fontSize: "0.9rem", lineHeight: 1.5, color: "var(--ak-text-secondary)", maxWidth: 260 }}>
              {translate(`${tp}.key`, { defaultMessage: "One dictionary per request - the key stays in the appliance's memory." } as any)}
            </span>
          </div>
          <DataCard mode="tarn" title={translate(`${tp}.ai.title`, { defaultMessage: "AI world" } as any)} tag={translate(`${tp}.ai.tag`, { defaultMessage: "Disguised only" } as any)} />
        </div>

        <ol style={{ listStyle: "none", margin: "32px 0 0", padding: 0, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))", gap: 12 }}>
          {steps.map((s, i) => (
            <li key={s} style={{ background: "var(--ak-surface)", border: `1px solid ${i === 3 ? "var(--ak-accent-border)" : "var(--ak-border)"}`, borderRadius: 12, padding: "14px 18px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ak-accent)", fontVariantNumeric: "tabular-nums" }}>{String(i + 1).padStart(2, "0")}</div>
              <div style={{ marginTop: 4, fontWeight: 700, color: "var(--ak-text)" }}>{s}</div>
            </li>
          ))}
        </ol>

        <p style={{ margin: "24px 0 0", fontSize: "1.02rem", lineHeight: 1.6, color: "var(--ak-text)" }}>
          <b>{translate(`${tp}.approval.pre`, { defaultMessage: "The AI proposes - you decide." } as any)}</b>{" "}
          <span style={{ color: "var(--ak-text-secondary)" }}>
            {translate(`${tp}.approval.desc`, {
              defaultMessage: "An authorised person approves every proposed action, your team implements it - and the approval is documented.",
            } as any)}
          </span>
        </p>
        <p style={{ margin: "12px 0 0", fontSize: "0.8rem", letterSpacing: "0.04em", color: "var(--ak-text-muted)" }}>
          {translate(`${tp}.note`, { defaultMessage: "Example data for illustration." } as any)}
        </p>
      </div>
    </section>
  );
};

export default AutonubilTarnkappe;
